import { useCallback, useEffect, useRef, useState } from "react";
import { Document as PdfDocument, Page } from "react-pdf";
import styles from "../../Styles/previewPDF.module.css";
import {
  LazyPdfDocumentGroupProps,
  LazyPdfPageDimensions,
  LazyPdfPageProps,
} from "../../Utils/Types";

const PAGE_LOAD_MARGIN = "600px 0px 600px 0px";
const DEFAULT_PAGE_WIDTH_AT_SCALE_1 = 600;
const DEFAULT_PAGE_HEIGHT_AT_SCALE_1 = 800;
const MAX_DEVICE_PIXEL_RATIO = 2;
// Retina phones report devicePixelRatio 2-3, which react-pdf multiplies into
// the canvas raster size on top of `scale`. Uncapped, a handful of
// concurrently mounted pages can exceed mobile browsers' total canvas memory
// ceiling and crash the tab, especially once the user zooms in and scrolls.
const EFFECTIVE_DEVICE_PIXEL_RATIO = Math.min(
  window.devicePixelRatio || 1,
  MAX_DEVICE_PIXEL_RATIO
);

/**
 * Only mounts the react-pdf <Page> (a rasterized canvas) while the page is
 * near the viewport. A placeholder of the same height keeps scroll position
 * stable while the page is unmounted, so 100+ page documents don't rasterize
 * every page at once and exhaust memory.
 */
function LazyPdfPage({ pageNumber, scale, dimensionCache }: LazyPdfPageProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsActive(entry.isIntersecting),
      { rootMargin: PAGE_LOAD_MARGIN }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleRenderSuccess = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const { width: measuredWidth, height: measuredHeight } =
      el.getBoundingClientRect();
    if (measuredWidth > 0 && measuredHeight > 0) {
      dimensionCache.current[pageNumber] = {
        width: measuredWidth / scale,
        height: measuredHeight / scale,
      };
    }
  }, [pageNumber, scale, dimensionCache]);

  const cachedDimensionsAtScale1 = dimensionCache.current[pageNumber];
  const placeholderWidth =
    (cachedDimensionsAtScale1?.width || DEFAULT_PAGE_WIDTH_AT_SCALE_1) * scale;
  const placeholderHeight =
    (cachedDimensionsAtScale1?.height || DEFAULT_PAGE_HEIGHT_AT_SCALE_1) *
    scale;

  return (
    <div ref={containerRef} className={styles.pdfPreviewPage}>
      {isActive ? (
        <Page
          pageNumber={pageNumber}
          scale={scale}
          devicePixelRatio={EFFECTIVE_DEVICE_PIXEL_RATIO}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          onRenderSuccess={handleRenderSuccess}
        />
      ) : (
        <div
          className={styles.pdfPagePlaceholder}
          style={{ width: placeholderWidth, height: placeholderHeight }}
        />
      )}
    </div>
  );
}

/**
 * Renders a PDF document with per-page virtualization: only pages near the
 * viewport are rasterized via react-pdf, so a single 100+ page document
 * doesn't mount hundreds of canvases at once and crash the tab/app.
 */
function LazyPdfDocumentGroup({ fileUrl, scale }: LazyPdfDocumentGroupProps) {
  const [numPages, setNumPages] = useState(0);
  const dimensionCache = useRef<Record<number, LazyPdfPageDimensions>>({});

  return (
    <PdfDocument
      file={fileUrl}
      onLoadSuccess={({ numPages }) => setNumPages(numPages)}
    >
      {Array.from(new Array(numPages), (_, pageIndex) => (
        <LazyPdfPage
          key={`p-${pageIndex + 1}`}
          pageNumber={pageIndex + 1}
          scale={scale}
          dimensionCache={dimensionCache}
        />
      ))}
    </PdfDocument>
  );
}

export default LazyPdfDocumentGroup;
